/* eslint-disable no-template-curly-in-string */
/* eslint-disable func-names */
import { format, toDate } from '@querodelivery/qd-date-helper';
import isString from 'lodash/isString';
import type { StringSchema, NumberSchema, MixedSchema } from 'yup';
import { setLocale, addMethod, string, number, mixed } from 'yup';
import type { MessageParams } from 'yup/lib/types';
import { isObjectId, validatePhoneBr } from '@/Utils/FnUtils';

import {
  invalidEmail,
  notValidValue,
  requiredErrorMessage,
  invalidPhone,
  invalidPositiveNumber,
  invalidYear,
  invalidCpf,
  invalidCnpj,
  invalidObjectId,
} from '@/_constants/form-messages';
import { validateCnpj, validateCpf } from '@/_utils/text';

setLocale({
  mixed: {
    required: requiredErrorMessage,
    notType: notValidValue,
  },
  string: {
    email: invalidEmail,
    max: 'Máximo de ${max} caracteres',
    min: 'Mínimo de ${min} caracteres',
    length: 'Deve ter ${length} caracteres',
  },
  number: {
    positive: invalidPositiveNumber,
  },
  date: {
    min: ({ min }: { min: string | Date } & MessageParams) =>
      `A data mínima deve ser depois de ${format(toDate(min), 'dd/MM/yyyy')}!`,
    max: ({ max }: { max: string | Date } & MessageParams) =>
      `A data máxima deve ser antes de ${format(toDate(max), 'dd/MM/yyyy')}!`,
  },
});

/* -------------------------------------------------------------------------- */
/*                              String functions                              */
/* -------------------------------------------------------------------------- */

addMethod<StringSchema>(string, 'telefone', function (errorMessage?: string) {
  return this.test(
    'telefone',
    errorMessage || invalidPhone,
    value => !value || !validatePhoneBr(value),
  );
});

addMethod<StringSchema>(string, 'objectId', function (errorMessage?: string) {
  return this.test(
    'objectId',
    errorMessage || invalidObjectId,
    value => !value || isObjectId(value),
  );
});

addMethod<StringSchema>(string, 'ano', function (errorMessage?: string) {
  return this.test(
    'ano',
    errorMessage || invalidYear,
    value => !value || value.length == 4,
  );
});

addMethod<StringSchema>(string, 'cpf', function (errorMessage?: string) {
  return this.test(
    'cpf',
    errorMessage || invalidCpf,
    value => !value || !validateCpf(value),
  );
});

addMethod<StringSchema>(string, 'cnpj', function (errorMessage?: string) {
  return this.test('cnpj', errorMessage || invalidCnpj, function (value) {
    if (!value) {
      return true;
    }
    const cnpjErrorMessage = validateCnpj(value);

    if (!cnpjErrorMessage) {
      return true;
    }

    return this.createError({ message: errorMessage ?? cnpjErrorMessage });
  });
});

addMethod<StringSchema>(
  string,
  'literalType',
  function ({
    literals,
    isOptional,
    errorMessage,
  }: {
    literals: string[];
    isOptional?: boolean;
    errorMessage?: string;
  }) {
    return this.test('literalType', errorMessage || 'type errado', value => {
      if (isOptional && !value) return true;

      const isStringInLiteralTypes = value && literals.indexOf(value) !== -1;
      return typeof value === 'string' && !!isStringInLiteralTypes;
    });
  },
);

/* -------------------------------------------------------------------------- */
/*                              Number functions                              */
/* -------------------------------------------------------------------------- */

addMethod<NumberSchema>(
  number,
  'positiveOr0',
  function (errorMessage?: string) {
    return this.test(
      'positiveOr0',
      errorMessage || invalidPositiveNumber,
      value => !value || !!number().moreThan(0).isValidSync(value),
    );
  },
);

/* -------------------------------------------------------------------------- */
/*                              Mixed functions                               */
/* -------------------------------------------------------------------------- */

addMethod<MixedSchema>(
  mixed,
  'stringOrArray',
  function (errorMessage?: string) {
    return this.test(
      'stringOrArray',
      errorMessage || requiredErrorMessage,
      value => Array.isArray(value) || isString(value),
    );
  },
);

export * from 'yup';
